import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { AutoAwesome as SparklesIcon } from "@mui/icons-material";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import Button from "../../components/Buttons/Button";
import ButtonMenu from "../../components/Buttons/ButtonMenu";
import TextField from "../../components/Form/TextField";
import AutocompleteField from "../../components/Form/AutocompleteField";
import Loader from "../../components/Loaders/Loader";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { getPathDatabaseByRole } from "../../actions/auth";
import { getKeyDatabase } from "../../services/public";
import {
  pruneCreativeTemplatePendingDeletes,
  startCreateEmptyCreativeTemplate,
  startDeleteCreativeTemplate,
  startDuplicateCreativeTemplate,
  startListenerCreativeTemplateCards,
  stopListenerCreativeTemplateCards,
} from "../../actions/creativeTemplates";
import { canWriteCreativeTemplates } from "../../features/creativeTemplates/utils/permissions";
import TemplateLayoutPreview from "../../features/creativeTemplates/editor/TemplateLayoutPreview";
import { canProduceCreativeTemplate } from "../../features/creativeTemplates/utils/layerTree";
import { ConfirmAlert, SimpleAlert } from "../../components/Alerts/Alerts";
import GalleryFolderBar from "../../components/Gallery/GalleryFolderBar";
import ModalCreateContentFolder from "../../components/Modal/ModalCreateContentFolder";
import ModalMoveContentsToFolder from "../../components/Modal/ModalMoveContentsToFolder";
import ModalRenameContentFolder from "../../components/Modal/ModalRenameContentFolder";
import { useCreativeTemplatesFolderSession } from "../../hooks/useCreativeTemplatesFolderSession";
import { useConfirmRemoveFromFolder } from "../../hooks/useFolderAwareDeleteAction";
import {
  getArtboardChipLabel,
  matchPresetID,
} from "../../features/creativeTemplates/utils/artboards";
import { getStripSlideSize } from "../../features/creativeTemplates/utils/continuousStripArtboards";
import { sortCreativeTemplateCards } from "../../features/creativeTemplates/utils/sortCreativeTemplateCards";
import useTemplatesAddon from "../../hooks/useTemplatesAddon";
import FeatureAddonPaywallDialog from "../../components/FeatureAddons/FeatureAddonPaywallDialog";
import { FEATURE_ADDON_KEYS } from "../../actions/featureAddons";
import { getCommonFolderID } from "../../utils/contentFolderUtils";
import {
  ALL_FILTER,
  BORDER_RADIUS,
  CREATIVE_TEMPLATE_ARTBOARD_PRESETS,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
  CREATIVE_TEMPLATE_LAYOUT_KIND,
  CREATIVE_TEMPLATE_SORT_NEWEST_FIRST,
} from "../../utils/constants";
import { PILL_TRIGGER_VARIANT } from "../../components/Buttons/PillTriggerButton";
import {
  BOX_SHADOW_FIELDS,
  BOX_SHADOW_LITTLE,
  INPUT_COLOR_BORDER,
  PRIMARY_COLOR_TEXT,
  TEMPLATES_MUTED_BG,
  TEMPLATES_MUTED_TEXT,
} from "../../utils/colors";
import { optionsCreativeTemplatesSort } from "../../utils/options";
import {
  BUTTON_CLOSE,
  BUTTON_EDIT,
  BUTTON_GO_BACK,
  GALLERY_FOLDERS_ALL,
  GALLERY_FOLDERS_MOVE,
  LOADING_INFORMATION,
  TEMPLATES_BUTTON_CREATE,
  TEMPLATES_CAROUSEL_CHIP,
  TEMPLATES_CONTINUOUS_CAROUSEL_CHIP,
  TEMPLATES_CONFIRM_DELETE_DESCRIPTION,
  TEMPLATES_CONFIRM_DELETE_TITLE,
  TEMPLATES_CREATE_ERROR,
  TEMPLATES_DELETE,
  GALLERY_FOLDERS_REMOVE_FROM_FOLDER,
  TEMPLATES_DELETE_ERROR,
  TEMPLATES_DESCRIPTION,
  TEMPLATES_DUPLICATE,
  TEMPLATES_EMPTY,
  TEMPLATES_FOLDER_EMPTY,
  TEMPLATES_ARTBOARD,
  TEMPLATES_NODE_COUNT,
  TEMPLATES_NODE_COUNT_SINGLE,
  TEMPLATES_PRODUCE_DRAFT_COMPLETE,
  TEMPLATES_PRODUCE_DRAFT_DESCRIPTION,
  TEMPLATES_PRODUCE_DRAFT_TITLE,
  TEMPLATES_PRODUCE_TITLE,
  TEMPLATES_SEARCH_NO_RESULTS,
  TEMPLATES_SEARCH_PLACEHOLDER,
  TEMPLATES_TITLE,
  TEMPLATES_UNAUTHORIZED,
  TEMPLATES_UNTITLED,
} from "../../i18n/keysTranslations";

const templateMetaChipSx = {
  backgroundColor: TEMPLATES_MUTED_BG,
  color: TEMPLATES_MUTED_TEXT,
  height: 24,
  flexShrink: 0,
  "& .MuiChip-label": { px: 1, fontSize: 12, color: TEMPLATES_MUTED_TEXT },
};

const produceCardButtonSx = {
  minWidth: 0,
  width: "100%",
  height: "auto",
  minHeight: 42,
  py: 1.25,
  fontSize: "13px !important",
  color: `${TEMPLATES_MUTED_TEXT} !important`,
  backgroundColor: `${TEMPLATES_MUTED_BG} !important`,
  border: "none !important",
  boxShadow: "none !important",
  "& .MuiButton-endIcon": {
    color: `${TEMPLATES_MUTED_TEXT} !important`,
  },
  "&.MuiButtonBase-root.MuiButton-root:hover": {
    color: `${TEMPLATES_MUTED_TEXT} !important`,
    backgroundColor: "#e4e4ea !important",
    border: "none !important",
    boxShadow: "none !important",
    "& .MuiButton-endIcon": {
      color: `${TEMPLATES_MUTED_TEXT} !important`,
    },
  },
};

const sxTemplateStripSurface = {
  backgroundColor: "#ffffff",
};

const sxTemplateActionStrip = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  width: "100%",
  flexShrink: 0,
  px: 1,
  py: 0.75,
};

const sxTemplateCardMenuButton = {
  width: 32,
  height: 32,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  color: "text.primary",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.9)",
  },
};

const getUniqueChipItems = ({ labels }) => {
  const items = [];
  const seenLabels = new Set();
  labels.forEach((label) => {
    if (
      !label ||
      seenLabels.has(label) ||
      label === CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID
    ) {
      return;
    }
    seenLabels.add(label);
    items.push({ key: label, label });
  });
  return items;
};

const SIZE_LABEL_PATTERN = /^(\d+)×(\d+)$/;

// Older continuous cards stored the whole strip size ("10240×2048") instead of
// the per-slide format, so convert it back to a preset label.
const toContinuousFormatLabel = ({ label, slideCount }) => {
  const match = SIZE_LABEL_PATTERN.exec(label);
  if (!match) return label;
  const slideSize = getStripSlideSize({
    artboard: { width: Number(match[1]), height: Number(match[2]) },
    slideCount,
  });
  const presetID = slideSize ? matchPresetID(slideSize) : null;
  return presetID ? CREATIVE_TEMPLATE_ARTBOARD_PRESETS[presetID].label : label;
};

const getCardArtboardChipItems = ({ card, t }) => {
  const isContinuous =
    card.layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL;
  const storedLabels = (
    Array.isArray(card.artboardFormats) ? card.artboardFormats : []
  ).map((label) =>
    isContinuous
      ? toContinuousFormatLabel({ label, slideCount: card.slideCount })
      : label
  );
  const storedItems = getUniqueChipItems({ labels: storedLabels });
  if (storedItems.length > 0) {
    return storedItems;
  }

  const artboardIDs = card.artboardIDs?.length
    ? card.artboardIDs
    : [card.artboard?.id].filter(Boolean);
  const computedItems = getUniqueChipItems({
    labels: artboardIDs.map((artboardID) =>
      getArtboardChipLabel({
        artboardID,
        cardArtboard: card.artboard,
      })
    ),
  });
  if (computedItems.length > 0) {
    return computedItems;
  }

  return [
    {
      key: "artboard-fallback",
      label: t(TEMPLATES_ARTBOARD, {
        width: card.artboard?.width || 0,
        height: card.artboard?.height || 0,
      }),
    },
  ];
};

const Templates = ({
  onOpenEditor = () => {},
  onOpenProduce = () => {},
  onCloseModal,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const pendingDeleteIDs = useSelectorApp(
    (state) => state.creativeTemplates?.pendingDeleteIDs || {}
  );
  const unsubscribeCardsRef = useRef(null);
  const { role } = dispatch(getPathDatabaseByRole());
  const canWrite = canWriteCreativeTemplates({ role });
  const [cards, setCards] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [folderFilterID, setFolderFilterID] = useState(ALL_FILTER);
  const [moveTemplateIDs, setMoveTemplateIDs] = useState([]);
  const {
    open: templatesPaywallOpen,
    status: templatesPaywallStatus,
    isLoading: templatesPaywallLoading,
    isCheckingOut: templatesCheckingOut,
    closePaywall: closeTemplatesPaywall,
    checkoutTier: checkoutTemplatesTier,
    blockIfCapacityReached,
  } = useTemplatesAddon();
  const { control: searchControl, watch: watchSearchForm } = useForm({
    defaultValues: {
      searchTemplates: "",
      sortOrder: CREATIVE_TEMPLATE_SORT_NEWEST_FIRST,
    },
  });
  const sortOrder = watchSearchForm("sortOrder");
  const clearMoveSelection = useCallback(() => {
    setMoveTemplateIDs([]);
  }, []);
  const {
    folders,
    activeFolderID,
    isLoadingFolders,
    isCreatingFolder,
    isMovingContents,
    isRenamingFolder,
    onSelectFolder,
    onDropMany,
    moveContents,
    isOpenCreateFolderModal,
    openCreateFolderModal,
    closeCreateFolderModal,
    isOpenMoveFolderModal,
    openMoveFolderModal,
    closeMoveFolderModal,
    onCreateFolderInContext,
    createFolderParentID,
    createFolderParentLabel,
    getFolderLabel,
    isOpenRenameFolderModal,
    openRenameFolderModal,
    closeRenameFolderModal,
    renameFolderTarget,
    onRenameFolder,
    onDeleteFolder,
  } = useCreativeTemplatesFolderSession({
    folderFilterID,
    onFolderFilterChange: setFolderFilterID,
    setCards,
    clearSelection: clearMoveSelection,
  });

  useEffect(() => {
    dispatch(
      startListenerCreativeTemplateCards({
        onUpdatedDataCallback: (value) => {
          const nextCards = value || {};
          setCards(nextCards);
          setIsLoading(false);
          dispatch(
            pruneCreativeTemplatePendingDeletes({
              existingIDs: Object.keys(nextCards),
            })
          );
        },
        onReferenceAvailable: (unsubscribe) => {
          unsubscribeCardsRef.current = unsubscribe;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCreativeTemplateCards({
          unsubscribe: unsubscribeCardsRef.current,
        })
      );
    };
  }, [dispatch]);

  const onCreateSingle = async () => {
    if (!canWrite) {
      SimpleAlert({
        title: t(TEMPLATES_UNAUTHORIZED),
        icon: "warning",
      });
      return;
    }

    if (
      await blockIfCapacityReached({
        liveCount: Object.keys(cards || {}).length,
      })
    ) {
      return;
    }

    setIsCreating(true);
    const response = await dispatch(
      startCreateEmptyCreativeTemplate({
        name: "",
        folderID: activeFolderID,
        layoutKind: CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE,
      })
    );
    setIsCreating(false);
    if (!response.ok) {
      SimpleAlert({
        title: t(TEMPLATES_CREATE_ERROR),
        icon: "error",
      });
      return;
    }
    onOpenEditor({
      templateID: response.data.templateID,
      isProvisionalCreate: true,
    });
  };

  const onDuplicate = async ({ templateID }) => {
    if (
      await blockIfCapacityReached({
        liveCount: Object.keys(cards || {}).length,
      })
    ) {
      return;
    }
    const response = await dispatch(
      startDuplicateCreativeTemplate({ templateID })
    );
    if (response.ok) {
      onOpenEditor({ templateID: response.data.templateID });
    }
  };

  const onDelete = async ({ templateID }) => {
    const confirmed = await ConfirmAlert({
      title: t(TEMPLATES_CONFIRM_DELETE_TITLE),
      text: t(TEMPLATES_CONFIRM_DELETE_DESCRIPTION),
    });
    if (!confirmed.isConfirmed) return;
    const response = await dispatch(
      startDeleteCreativeTemplate({ templateID })
    );
    if (!response.ok) {
      SimpleAlert({
        title: t(TEMPLATES_DELETE_ERROR),
        icon: "error",
      });
    }
  };

  const onCloseMoveFolderModal = () => {
    closeMoveFolderModal();
    clearMoveSelection();
  };

  const onOpenMoveFolderModal = ({ templateID }) => {
    setMoveTemplateIDs([templateID]);
    openMoveFolderModal();
  };

  const movingItems = useMemo(
    () =>
      moveTemplateIDs
        .map((templateID) => cards[templateID])
        .filter((card) => card?.id),
    [cards, moveTemplateIDs]
  );

  const disabledMoveFolderID = useMemo(
    () => getCommonFolderID({ items: movingItems }),
    [movingItems]
  );

  const currentFolderLabelForMove = useMemo(() => {
    if (disabledMoveFolderID) {
      return getFolderLabel({ folderID: disabledMoveFolderID });
    }
    if (activeFolderID) {
      return getFolderLabel({ folderID: activeFolderID });
    }
    return t(GALLERY_FOLDERS_ALL);
  }, [activeFolderID, disabledMoveFolderID, getFolderLabel, t]);

  const onRemoveTemplateFromFolder = useConfirmRemoveFromFolder(
    ({ templateID }) =>
      moveContents({ contentIDs: [templateID], folderID: null })
  );

  const onCallbackMoveToFolder = async ({ folderID }) =>
    moveContents({
      contentIDs: moveTemplateIDs,
      folderID,
    });

  const allList = useMemo(
    () =>
      sortCreativeTemplateCards({
        cards: Object.entries(cards)
          .map(([id, card]) => ({
            ...card,
            id: card?.id || id,
          }))
          .filter((card) => card.id && !pendingDeleteIDs[card.id]),
        sortOrder,
      }),
    [cards, pendingDeleteIDs, sortOrder]
  );

  const folderList = useMemo(() => {
    if (folderFilterID === ALL_FILTER) {
      return allList;
    }
    return allList.filter((card) => card.folderID === folderFilterID);
  }, [allList, folderFilterID]);

  const filteredList = useMemo(() => {
    const term = String(searchTerm).toLowerCase().trim();
    if (!term) {
      return folderList;
    }

    return folderList.filter(({ name = "" }) =>
      String(name).toLowerCase().includes(term)
    );
  }, [folderList, searchTerm]);

  return (
    <ContainerPage
      hasMarginBottom={false}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "100%",
        flex: 1,
        px: 0,
        pt: 0,
        pb: 0,
        alignItems: "stretch",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          width: "100%",
          pt: 3,
          pb: isLoading ? 3 : 0,
          px: 3,
          position: "sticky",
          top: 0,
          zIndex: 3,
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TitlePage>{t(TEMPLATES_TITLE)}</TitlePage>
              <SparklesIcon
                sx={{
                  fontSize: { xs: 24, sm: 32 },
                  color: "primary.main",
                }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {canWrite ? (
                <Button
                  color="primary"
                  onClick={onCreateSingle}
                  loading={isCreating}
                  startIcon={<AddIcon />}
                >
                  {t(TEMPLATES_BUTTON_CREATE)}
                </Button>
              ) : null}
              {onCloseModal ? (
                <Button
                  iconOnly
                  color="secondary"
                  className="close-modal-button"
                  ariaLabel={t(BUTTON_CLOSE)}
                  onClick={onCloseModal}
                  sx={{
                    color: `${PRIMARY_COLOR_TEXT} !important`,
                    backgroundColor: "#ffffff !important",
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    boxShadow: BOX_SHADOW_LITTLE,
                    "& .MuiSvgIcon-root": {
                      color: `${PRIMARY_COLOR_TEXT} !important`,
                    },
                    "&:hover, &.MuiButtonBase-root.MuiButton-root:hover": {
                      color: `${PRIMARY_COLOR_TEXT} !important`,
                      backgroundColor: "#ffffff !important",
                      border: `1px solid ${INPUT_COLOR_BORDER} !important`,
                      boxShadow: BOX_SHADOW_LITTLE,
                      filter: "none",
                      "& .MuiSvgIcon-root": {
                        color: `${PRIMARY_COLOR_TEXT} !important`,
                      },
                    },
                  }}
                >
                  <CloseIcon />
                </Button>
              ) : null}
            </Box>
          </Box>
          <Typography
            color="text.secondary"
            sx={{
              width: "100%",
              maxWidth: "min(100%, 560px)",
            }}
          >
            {t(TEMPLATES_DESCRIPTION)}
          </Typography>
        </Box>
        {!isLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            {allList.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  width: "100%",
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <TextField
                  name="searchTemplates"
                  control={searchControl}
                  placeholder={t(TEMPLATES_SEARCH_PLACEHOLDER)}
                  variant="filled"
                  fullWidth={false}
                  rules={{}}
                  actionsChange={(value) => setSearchTerm(value)}
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "100%", sm: 420 },
                    "& .MuiInputBase-root": {
                      border: `1px solid ${INPUT_COLOR_BORDER}`,
                      borderRadius: BORDER_RADIUS + 1,
                      boxShadow: BOX_SHADOW_FIELDS,
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                    },
                  }}
                  startIcon={
                    <SearchIcon
                      sx={{ color: "#999999 !important", fontSize: 20 }}
                    />
                  }
                />
                <AutocompleteField
                  name="sortOrder"
                  control={searchControl}
                  options={optionsCreativeTemplatesSort}
                  doTranslate
                  fullWidth={false}
                  rules={{ required: false }}
                  mode="text"
                  triggerVariant={PILL_TRIGGER_VARIANT.PILL}
                  sx={{ maxWidth: "100%", flexShrink: 0 }}
                />
              </Box>
            ) : null}
            <Box
              sx={{
                width: "100%",
                py: allList.length > 0 ? 2 : 0,
              }}
            >
              <GalleryFolderBar
                folders={folders}
                pinBottomPadding
                activeFolderID={activeFolderID}
                selectedContentIDs={moveTemplateIDs}
                canEdit={canWrite}
                isLoading={
                  isLoadingFolders || isCreatingFolder || isMovingContents
                }
                onSelectFolder={onSelectFolder}
                onOpenCreateFolder={openCreateFolderModal}
                onDropMany={onDropMany}
                onRenameFolder={openRenameFolderModal}
                onDeleteFolder={onDeleteFolder}
              />
            </Box>
          </Box>
        ) : null}
      </Box>
      <Box sx={{ width: "100%", px: 3, pt: isLoading ? 2 : 0, pb: 3 }}>
        {isLoading ? (
          <Loader hasMessage message={t(LOADING_INFORMATION)} />
        ) : allList.length === 0 ? (
          <Typography color="text.secondary">{t(TEMPLATES_EMPTY)}</Typography>
        ) : folderList.length === 0 ? (
          <Typography color="text.secondary">
            {t(TEMPLATES_FOLDER_EMPTY)}
          </Typography>
        ) : filteredList.length === 0 ? (
          <Typography color="text.secondary">
            {t(TEMPLATES_SEARCH_NO_RESULTS)}
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
                xl: "repeat(5, minmax(0, 1fr))",
              },
              gap: 2,
              pb: 5,
              width: "100%",
            }}
          >
            {filteredList.map((card) => {
              const artboardChipItems = getCardArtboardChipItems({ card, t });
              const layerCount = card.nodeCount || 0;
              const layerLabel = t(
                layerCount === 1
                  ? TEMPLATES_NODE_COUNT_SINGLE
                  : TEMPLATES_NODE_COUNT,
                { count: layerCount }
              );
              const cardMenuOptions = [
                {
                  label: BUTTON_EDIT,
                  onClick: () => onOpenEditor({ templateID: card.id }),
                },
                {
                  label: TEMPLATES_DUPLICATE,
                  onClick: () => onDuplicate({ templateID: card.id }),
                },
                {
                  label: GALLERY_FOLDERS_MOVE,
                  onClick: () => onOpenMoveFolderModal({ templateID: card.id }),
                },
                activeFolderID
                  ? {
                      label: GALLERY_FOLDERS_REMOVE_FROM_FOLDER,
                      onClick: () =>
                        onRemoveTemplateFromFolder({ templateID: card.id }),
                    }
                  : {
                      label: TEMPLATES_DELETE,
                      onClick: () => onDelete({ templateID: card.id }),
                    },
              ];

              return (
                <Box
                  key={card.id}
                  draggable={canWrite}
                  onDragStart={(event) => {
                    event.dataTransfer.setData("text/plain", card.id);
                    event.dataTransfer.effectAllowed = "move";
                  }}
                  sx={{
                    boxShadow: BOX_SHADOW_LITTLE,
                    borderRadius: 3,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    bgcolor: "#fff",
                  }}
                >
                  <Box
                    sx={{ position: "relative", width: "100%", flexShrink: 0 }}
                  >
                    <Box
                      onClick={() => onOpenEditor({ templateID: card.id })}
                      sx={{ cursor: "pointer" }}
                    >
                      <TemplateLayoutPreview
                        card={card}
                        // Continuous thumbnails are the whole strip: crop to slide 1.
                        fallbackSize={
                          card.layoutKind ===
                          CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
                            ? "auto 100%"
                            : "cover"
                        }
                        fallbackPosition={
                          card.layoutKind ===
                          CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
                            ? "left center"
                            : "center"
                        }
                      />
                    </Box>
                    {canWrite ? (
                      <Box
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 1,
                        }}
                      >
                        <ButtonMenu
                          mode="icon"
                          iconButton={<MoreHorizIcon fontSize="small" />}
                          options={cardMenuOptions}
                          edge={false}
                          sx={sxTemplateCardMenuButton}
                        />
                      </Box>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      ...sxTemplateActionStrip,
                      flexDirection: "column",
                      alignItems: "stretch",
                      gap: 1.5,
                      borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                      ...sxTemplateStripSurface,
                      px: 2,
                      py: 2,
                    }}
                  >
                    <Typography sx={{ fontWeight: 500 }} noWrap>
                      {card.name?.trim() || t(TEMPLATES_UNTITLED)}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        gap: 0.5,
                        overflow: "hidden",
                        minWidth: 0,
                      }}
                    >
                      {artboardChipItems.map(({ key, label }) => (
                        <Chip
                          key={key}
                          label={label}
                          size="small"
                          sx={templateMetaChipSx}
                        />
                      ))}
                      {card.layoutKind ===
                      CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL ? (
                        <Chip
                          key="carousel"
                          label={t(TEMPLATES_CAROUSEL_CHIP)}
                          size="small"
                          sx={templateMetaChipSx}
                        />
                      ) : null}
                      {card.layoutKind ===
                      CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL ? (
                        <Chip
                          key="continuous-carousel"
                          label={t(TEMPLATES_CONTINUOUS_CAROUSEL_CHIP)}
                          size="small"
                          sx={templateMetaChipSx}
                        />
                      ) : null}
                      <Chip
                        key="layer-count"
                        label={layerLabel}
                        size="small"
                        sx={templateMetaChipSx}
                      />
                    </Box>
                    <Button
                      color="secondary"
                      endIcon={<ChevronRightIcon />}
                      onClick={async () => {
                        const canProduce = canProduceCreativeTemplate({
                          name: card.name,
                          nodeCount: card.nodeCount,
                        });
                        if (!canProduce) {
                          const confirmed = await ConfirmAlert({
                            title: t(TEMPLATES_PRODUCE_DRAFT_TITLE),
                            text: t(TEMPLATES_PRODUCE_DRAFT_DESCRIPTION),
                            confirmButtonText: t(
                              TEMPLATES_PRODUCE_DRAFT_COMPLETE
                            ),
                            cancelButtonText: t(BUTTON_GO_BACK),
                            icon: "warning",
                          });
                          if (confirmed.isConfirmed) {
                            onOpenEditor({ templateID: card.id });
                          }
                          return;
                        }
                        const produceID = dispatch(getKeyDatabase());
                        onOpenProduce({
                          templateID: card.id,
                          produceID,
                        });
                      }}
                      sx={produceCardButtonSx}
                    >
                      {t(TEMPLATES_PRODUCE_TITLE)}
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      <ModalCreateContentFolder
        modalOpen={isOpenCreateFolderModal}
        folders={folders}
        parentFolderID={createFolderParentID}
        parentFolderLabel={createFolderParentLabel}
        isLoading={isCreatingFolder}
        onCloseModal={closeCreateFolderModal}
        onCallbackCreate={onCreateFolderInContext}
      />
      <ModalMoveContentsToFolder
        modalOpen={isOpenMoveFolderModal}
        folders={folders}
        currentFolderLabel={currentFolderLabelForMove}
        createParentFolderID={createFolderParentID}
        createParentFolderLabel={createFolderParentLabel}
        disabledFolderID={disabledMoveFolderID}
        isLoading={isMovingContents}
        isCreatingFolder={isCreatingFolder}
        onCloseModal={onCloseMoveFolderModal}
        onCallbackCreate={onCreateFolderInContext}
        onCallbackMove={onCallbackMoveToFolder}
      />
      <ModalRenameContentFolder
        modalOpen={isOpenRenameFolderModal}
        folders={folders}
        folder={renameFolderTarget}
        isLoading={isRenamingFolder}
        onCloseModal={closeRenameFolderModal}
        onCallbackRename={onRenameFolder}
      />
      <FeatureAddonPaywallDialog
        open={templatesPaywallOpen}
        onClose={closeTemplatesPaywall}
        status={templatesPaywallStatus}
        addonKey={FEATURE_ADDON_KEYS.TEMPLATES}
        isLoading={templatesPaywallLoading}
        isCheckingOut={templatesCheckingOut}
        onCheckoutTier={checkoutTemplatesTier}
      />
    </ContainerPage>
  );
};

export default Templates;
